import { useState, useEffect } from 'react';
import { allPosts, Post } from '@/mocks/allPosts';

const EPOCH = new Date('2025-01-01T00:00:00');
const PUBLISH_HOURS = [8, 13, 19];
const SLOT_LABELS = ['Morning', 'Afternoon', 'Evening'];
const SLOT_ICONS = ['ri-sun-line', 'ri-sun-foggy-line', 'ri-moon-line'];
const SLOT_COLORS = ['text-amber-500', 'text-orange-400', 'text-indigo-400'];
const SLOT_BG = ['bg-amber-50 dark:bg-amber-900/20', 'bg-orange-50 dark:bg-orange-900/20', 'bg-indigo-50 dark:bg-indigo-900/20'];
const SLOT_BORDER = ['border-amber-200 dark:border-amber-800', 'border-orange-200 dark:border-orange-800', 'border-indigo-200 dark:border-indigo-800'];

const CATEGORY_COLORS: Record<string, string> = {
  dating: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  marriage: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  breakups: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  communication: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
  'self-love': 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
};

function getPublishDateTime(publishIndex: number): Date {
  const dayIndex = Math.floor(publishIndex / 3);
  const slotIndex = publishIndex % 3;
  const date = new Date(EPOCH);
  date.setDate(date.getDate() + dayIndex);
  date.setHours(PUBLISH_HOURS[slotIndex], 0, 0, 0);
  return date;
}

interface ScheduledPost {
  post: Post;
  publishAt: Date;
  slotIndex: number;
  isPast: boolean;
  isToday: boolean;
  isTomorrow: boolean;
}

interface DayGroup {
  label: string;
  date: Date;
  posts: ScheduledPost[];
}

function groupByDay(items: ScheduledPost[]): DayGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const map: Record<string, DayGroup> = {};

  items.forEach((item) => {
    const d = item.publishAt;
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!map[dayKey]) {
      const dayTime = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const diff = Math.round((dayTime - today) / 86400000);
      let label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
      if (diff === 0) label = 'Today — ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (diff === -1) label = 'Yesterday — ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (diff === 1) label = 'Tomorrow — ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      map[dayKey] = { label, date: d, posts: [] };
    }
    map[dayKey].posts.push(item);
  });

  return Object.values(map).sort((a, b) => a.date.getTime() - b.date.getTime());
}

function useCountdown(targetDate: Date): string {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const diff = targetDate.getTime() - Date.now();
  if (diff <= 0) return 'Published';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function CountdownBadge({ target }: { target: Date }) {
  const label = useCountdown(target);
  const isNear = target.getTime() - Date.now() < 3600000 && target.getTime() > Date.now();
  return (
    <span className={`text-xs font-mono px-2 py-0.5 rounded-full whitespace-nowrap ${
      isNear
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 animate-pulse'
        : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
    }`}>
      {label}
    </span>
  );
}

type ViewMode = 'upcoming' | 'recent' | 'all';

export default function PostSchedule() {
  const now = new Date();
  const [viewMode, setViewMode] = useState<ViewMode>('upcoming');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const allScheduled: ScheduledPost[] = allPosts.map((post) => {
    const publishAt = getPublishDateTime(post.publishIndex);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const pubDay = new Date(publishAt.getFullYear(), publishAt.getMonth(), publishAt.getDate()).getTime();
    const diff = Math.round((pubDay - today) / 86400000);
    return {
      post,
      publishAt,
      slotIndex: post.publishIndex % 3,
      isPast: publishAt < now,
      isToday: diff === 0,
      isTomorrow: diff === 1,
    };
  });

  const publishedCount = allScheduled.filter((p) => p.isPast).length;
  const upcomingCount = allScheduled.filter((p) => !p.isPast).length;
  const todayCount = allScheduled.filter((p) => p.isToday).length;

  // Next upcoming post
  const nextPost = allScheduled
    .filter((p) => !p.isPast)
    .sort((a, b) => a.publishAt.getTime() - b.publishAt.getTime())[0];

  const filtered = allScheduled.filter((p) => {
    if (viewMode === 'upcoming') return !p.isPast;
    if (viewMode === 'recent') return p.isPast;
    return true;
  }).sort((a, b) => {
    if (viewMode === 'recent') return b.publishAt.getTime() - a.publishAt.getTime();
    return a.publishAt.getTime() - b.publishAt.getTime();
  });

  // For 'recent', only show last 7 days; for 'all', show all; for 'upcoming', show next 14 days
  const displayItems = viewMode === 'recent'
    ? filtered.slice(0, 21) // last 7 days * 3
    : viewMode === 'upcoming'
    ? filtered.slice(0, 30)
    : filtered;

  const grouped = groupByDay(displayItems);

  const toggleDay = (label: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-100 dark:border-stone-800 overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-stone-100 dark:border-stone-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300 flex items-center gap-2">
            <i className="ri-calendar-schedule-line text-rose-500" />
            Post Schedule
          </h3>
          <div className="flex items-center gap-1 bg-stone-100 dark:bg-stone-800 rounded-lg p-1">
            {(['upcoming', 'recent', 'all'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`text-xs px-3 py-1 rounded-md transition-colors cursor-pointer capitalize whitespace-nowrap ${
                  viewMode === mode
                    ? 'bg-white dark:bg-stone-700 text-stone-800 dark:text-stone-100 font-semibold shadow-sm'
                    : 'text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-stone-50 dark:bg-stone-800 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold font-mono text-stone-800 dark:text-stone-100">{publishedCount}</div>
            <div className="text-xs text-stone-400 mt-0.5">Published</div>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{todayCount}</div>
            <div className="text-xs text-stone-400 mt-0.5">Today</div>
          </div>
          <div className="bg-rose-50 dark:bg-rose-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold font-mono text-rose-500">{upcomingCount}</div>
            <div className="text-xs text-stone-400 mt-0.5">Upcoming</div>
          </div>
        </div>

        {/* Next publish countdown */}
        {nextPost && (
          <div className="mt-3 flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Next post publishing</div>
              <div className="text-xs text-stone-500 dark:text-stone-400 truncate">{nextPost.post.title}</div>
            </div>
            <CountdownBadge target={nextPost.publishAt} />
          </div>
        )}
      </div>

      {/* Schedule list */}
      <div className="divide-y divide-stone-50 dark:divide-stone-800/50 max-h-[520px] overflow-y-auto">
        {grouped.length === 0 && (
          <div className="py-12 text-center text-stone-400 text-sm">
            <i className="ri-calendar-line text-3xl mb-2 block" />
            No posts in this range
          </div>
        )}

        {grouped.map((day) => {
          const dayKey = day.label;
          const isExpanded = expandedDays.has(dayKey);
          const isToday = day.label.startsWith('Today');
          const isYesterday = day.label.startsWith('Yesterday');
          const isTomorrow = day.label.startsWith('Tomorrow');

          return (
            <div key={dayKey}>
              {/* Day header */}
              <button
                onClick={() => toggleDay(dayKey)}
                className="w-full flex items-center justify-between px-5 py-2.5 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  {isToday && (
                    <span className="text-xs font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full">TODAY</span>
                  )}
                  {isTomorrow && (
                    <span className="text-xs font-semibold bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 px-2 py-0.5 rounded-full">NEXT</span>
                  )}
                  {isYesterday && (
                    <span className="text-xs font-semibold bg-stone-200 text-stone-500 dark:bg-stone-700 dark:text-stone-400 px-2 py-0.5 rounded-full">YESTERDAY</span>
                  )}
                  <span className="text-xs font-semibold text-stone-600 dark:text-stone-400">{day.label.replace(/^(Today|Tomorrow|Yesterday) — /, '')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-400">{day.posts.length} post{day.posts.length !== 1 ? 's' : ''}</span>
                  {isExpanded
                    ? <i className="ri-arrow-up-s-line text-stone-400 text-sm" />
                    : <i className="ri-arrow-down-s-line text-stone-400 text-sm" />
                  }
                </div>
              </button>

              {/* Posts for this day */}
              {(isExpanded || isToday || isTomorrow) && (
                <div className="px-4 pb-3 space-y-2">
                  {day.posts.map((item) => {
                    const slotColor = SLOT_COLORS[item.slotIndex];
                    const slotBg = SLOT_BG[item.slotIndex];
                    const slotBorder = SLOT_BORDER[item.slotIndex];
                    const catColor = CATEGORY_COLORS[item.post.category] ?? 'bg-stone-100 text-stone-600';
                    const timeStr = item.publishAt.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });

                    return (
                      <div
                        key={item.post.id}
                        className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-opacity ${
                          item.isPast ? 'opacity-60' : ''
                        } ${slotBg} ${slotBorder}`}
                      >
                        {/* Time slot */}
                        <div className={`flex-shrink-0 flex flex-col items-center gap-0.5 w-14 pt-0.5 ${slotColor}`}>
                          <i className={`${SLOT_ICONS[item.slotIndex]} text-base`} />
                          <span className="text-xs font-mono font-semibold">{timeStr}</span>
                          <span className="text-xs opacity-70">{SLOT_LABELS[item.slotIndex]}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium capitalize ${catColor}`}>
                              {item.post.category}
                            </span>
                            {item.isPast && (
                              <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                <i className="ri-check-line text-xs" /> Published
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-semibold text-stone-700 dark:text-stone-200 mt-1 leading-snug line-clamp-2">
                            {item.post.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-stone-400">{item.post.author}</span>
                            <span className="text-stone-300 dark:text-stone-600">·</span>
                            <span className="text-xs text-stone-400">{item.post.readTime} min read</span>
                          </div>
                        </div>

                        {/* Right: countdown or check */}
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          {item.isPast ? (
                            <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                              <i className="ri-check-fill text-xs text-emerald-600 dark:text-emerald-400" />
                            </div>
                          ) : (
                            <CountdownBadge target={item.publishAt} />
                          )}
                          <span className="text-xs text-stone-400 font-mono">#{item.post.publishIndex}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="px-5 py-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between">
        <span className="text-xs text-stone-400">
          <i className="ri-time-line mr-1" />
          Publishes at 8:00am, 1:00pm &amp; 7:00pm daily
        </span>
        <span className="text-xs text-stone-400 font-mono">{allPosts.length} total posts</span>
      </div>
    </div>
  );
}
